/**
 *
 * MultiSelect
 *
 */

import React, { memo, useMemo, useState, useRef, useEffect } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import ReactDOM from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Components
import Checkbox from 'components/Checkbox'

// Utils
import { COLORS } from 'global-styles'

const TriggerShell = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    width: 100%;
    min-height: 2.375rem;
    background: ${COLORS.white};
    border: 1px solid ${COLORS.borderGrey};
    border-radius: 0.25rem;
    padding: 0.25rem 0.5rem;
    text-decoration: none;
    cursor: pointer;
`

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    margin: 0.125rem 0.25rem 0.125rem 0;
    padding: 0.125rem 0.375rem;
    font-size: 0.75rem;
    line-height: 1;
    background: ${COLORS.brand_highlight};
    color: ${COLORS.black};
    border: 1px solid ${COLORS.grey_d};
    border-radius: 999rem;
    user-select: none;
`

const List = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 18.75rem;
    overflow-y: auto;
`

const Row = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    background: transparent;
    &:hover { background: ${COLORS.brand_highlight}; }
    &.disabled { opacity: 0.5; }
`

const PortalMenu = styled.div`
    position: fixed;
    z-index: 11000;
    top: 0;
    left: 0;
    min-width: 10rem;
    padding: 0.5rem 0;
    margin: 0.125rem 0 0;
    font-size: 1rem;
    color: ${COLORS.black};
    background-color: ${COLORS.white};
    border: 1px solid ${COLORS.grey_d};
    border-radius: 0.25rem;
    box-shadow: 0 0.5rem 1.5rem rgba(0,0,0,.12);
`

function MultiSelect({
	options,
	value,
	onChange,
	placeholder = 'Select options',
	show_values = true,
	searchable = true,
	class_name,
	item_class_name,
	item_data_test_id,
	error,
	disabled,
	max_selected = Infinity,
	creatable = false,
	onCreate,
	dropdown_style,
}) {
	const [search_query, setSearchQuery] = useState('')
	const [locally_created_options, setLocallyCreatedOptions] = useState([])
	const [is_open, setIsOpen] = useState(false)

	const trigger_ref = useRef(null)
	const menu_ref = useRef(null)
	const [portal_position, setPortalPosition] = useState({ top: 0, left: 0, width: 0 })

	const merged_options = useMemo(() => {
		const options_by_value = new Map(options.map((option) => [option.value, option]))
		locally_created_options.forEach((option) => {
			if (!options_by_value.has(option.value)) options_by_value.set(option.value, option)
		})
		return Array.from(options_by_value.values())
	}, [options, locally_created_options])

	const filtered_options = useMemo(() => {
		const normalized_query = search_query.trim().toLowerCase()
		if (!normalized_query) return merged_options
		return merged_options.filter((option) => {
			const label_lower = (option.label || '').toLowerCase()
			const value_lower = (option.value || '').toLowerCase()
			const addon_lower = option.addon != null ? String(option.addon).toLowerCase() : ''
			return (
				label_lower.includes(normalized_query) ||
				value_lower.includes(normalized_query) ||
				addon_lower.includes(normalized_query)
			)
		})
	}, [merged_options, search_query])

	const selected_options = useMemo(
		() =>
			value.map(
				(selected_value) =>
					merged_options.find((option) => option.value === selected_value) ||
					({ label: selected_value, value: selected_value }),
			),
		[merged_options, value],
	)

	function toggleOption(selected_value) {
		const already_selected = value.includes(selected_value)
		if (already_selected) return onChange(value.filter((existing) => existing !== selected_value))
		if (value.length >= max_selected) return
		onChange([...value, selected_value])
	}

	useEffect(() => {
		if (!is_open) return

		const recalcPosition = () => {
			const trigger_rect = trigger_ref.current?.getBoundingClientRect()
			if (!trigger_rect) return

			let top = trigger_rect.bottom
			let left = trigger_rect.left
			const width = trigger_rect.width

			// Measure menu if present to decide flip/clamp
			const menu_element = menu_ref.current
			if (menu_element) {
				const { height } = menu_element.getBoundingClientRect()
				const space_below = window.innerHeight - trigger_rect.bottom
				if (height > space_below) {
					// Flip above
					top = trigger_rect.top - height
				}

				// Clamp horizontally to viewport
				const max_left = window.innerWidth - width
				left = Math.min(left, max_left)
			}
			setPortalPosition({ top, left, width })
		}
		recalcPosition()

		// Close on outside click (capture to beat modal stopPropagation)
		const handlePointerDownCapture = (event) => {
			const target = event.target
			if (menu_ref.current?.contains(target)) return
			if (trigger_ref.current?.contains(target)) return
			setIsOpen(false)
		}

		const handleKeyDown = (event) => {
			if (event.key === 'Escape') setIsOpen(false)
		}

		window.addEventListener('resize', recalcPosition)
		window.addEventListener('scroll', recalcPosition, true)
		document.addEventListener('pointerdown', handlePointerDownCapture, true)
		document.addEventListener('keydown', handleKeyDown)

		return () => {
			window.removeEventListener('resize', recalcPosition)
			window.removeEventListener('scroll', recalcPosition, true)
			document.removeEventListener('pointerdown', handlePointerDownCapture, true)
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [is_open])


	const normalized_query = search_query.trim()
	const can_create_option =
		!!creatable &&
		!!normalized_query &&
		!merged_options.some(
			(option) =>
				option.value === normalized_query.toLowerCase() ||
				(option.label || '').toLowerCase() === normalized_query.toLowerCase(),
		) &&
		value.length < max_selected

	const menuContent = (
		<div
			role="listbox"
			aria-multiselectable="true"
			onKeyDown={(event) => {
				if (event.key === 'Enter' && creatable) {
					event.preventDefault()
					const entered_text = search_query.trim()
					if (!entered_text) return
					const new_value = entered_text.toLowerCase()
					const already_exists = merged_options.some(
						(option) =>
							option.value === new_value || (option.label || '').toLowerCase() === entered_text.toLowerCase(),
					)
					if (!already_exists && value.length < max_selected) {
						onCreate?.(new_value)
						setLocallyCreatedOptions((previous) =>
							previous.find((option) => option.value === new_value)
								? previous
								: [...previous, { label: new_value, value: new_value }],
						)
						onChange([...value, new_value])
						setSearchQuery('')
					}
				}
			}}
		>
			{searchable && (
				<div className="p-2">
					<input
						type="text"
						className="form-control"
						placeholder="Search..."
						value={search_query}
						onChange={(event) => setSearchQuery(event.target.value)}
						autoFocus
					/>
				</div>
			)}

			{filtered_options.length > 0 ? (
				<List>
					{filtered_options.map((option) => {
						const is_checked = value.includes(option.value)
						const disabled_by_limit = !is_checked && value.length >= max_selected
						const label_content = (
							<>
								{option.addon !== undefined && <b>{option.addon} - </b>}
								{option.label}
								{show_values ? ` – ${option.value}` : ''}
							</>
						)
						return (
							<li
								key={option.value}
								className={item_class_name}
								data-testid={item_data_test_id ? item_data_test_id(option) : undefined}
							>
								<Row className={disabled_by_limit ? 'disabled' : ''}>
									<Checkbox
										label={label_content}
										name={`ms-${option.value}`}
										checked={is_checked}
										disabled={disabled_by_limit}
										onChange={() => toggleOption(option.value)}
									/>
								</Row>
							</li>
						)
					})}
				</List>
			) : (
				<div className="p-2 text-muted">
					{normalized_query ? (
						can_create_option ? (
							<>
								No matches found — press <b>Enter</b> to create “{normalized_query}”.
							</>
						) : (
							<>No matches found.</>
						)
					) : (
						<>No matches found.</>
					)}
				</div>
			)}
		</div>
	)

	return (
		<div className={class_name} style={{ width: '100%' }}>
			<TriggerShell
				ref={trigger_ref}
				style={{ borderColor: error ? '#dc3545' : undefined, pointerEvents: disabled ? 'none' : 'auto' }}
				onClick={() => {
					if (!disabled) setIsOpen((previousOpen) => !previousOpen)
				}}
			>
				<div className="d-flex align-items-center flex-wrap">
					{value.length ? (
						<>
							{selected_options.map((selected_option) => (
								<Badge key={selected_option.value}>
									{selected_option.addon !== undefined && <b>{selected_option.addon} - </b>}
									{selected_option.label}
									{show_values ? ` – ${selected_option.value}` : ''}
									<span
										role="button"
										className="ml-2"
										onClick={(event) => {
											event.stopPropagation()
											onChange(value.filter((existing) => existing !== selected_option.value))
										}}
										aria-label={`Remove ${selected_option.label}`}
										title="Remove"
										style={{ cursor: 'pointer' }}
									>
										×
									</span>
								</Badge>
							))}
						</>
					) : (
						<span className="text-muted">{placeholder}</span>
					)}
				</div>
				<FontAwesomeIcon icon="chevron-down" className="text-muted ml-2" />
			</TriggerShell>

			{is_open &&
				ReactDOM.createPortal(
					<PortalMenu
						ref={menu_ref}
						style={{
							top: portal_position.top,
							left: portal_position.left,
							minWidth: portal_position.width,
							...(dropdown_style || {}),
						}}
					>
						{menuContent}
					</PortalMenu>,
					document.body,
				)}
		</div>
	)
}

MultiSelect.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.string.isRequired,
			addon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		}),
	).isRequired,
	value: PropTypes.arrayOf(PropTypes.string).isRequired,
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	show_values: PropTypes.bool,
	searchable: PropTypes.bool,
	class_name: PropTypes.string,
	item_class_name: PropTypes.string,
	item_data_test_id: PropTypes.func,
	error: PropTypes.string,
	disabled: PropTypes.bool,
	max_selected: PropTypes.number,
	creatable: PropTypes.bool,
	onCreate: PropTypes.func,
	dropdown_style: PropTypes.object,
}

export default memo(MultiSelect)
